import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Sparkles, ArrowLeft, Calendar, Check, MessageCircle, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingForm = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [bookingSuccess, setBookingSuccess] = useState(null);
  const [businessWhatsapp, setBusinessWhatsapp] = useState('8920443069');

  useEffect(() => {
    axios.get(`${API}/payments/info`)
      .then((res) => res.data?.business_whatsapp && setBusinessWhatsapp(res.data.business_whatsapp))
      .catch(() => {});
  }, []);
  const [formData, setFormData] = useState({
    name: user.name || '',
    age: '',
    location: '',
    phone: user.phone || '',
    whatsapp: user.phone || '',
    occupation: '',
    health_issues: '',
    session_type: '',
    session_date: ''
  });

  // Capture lead when user fills any field
  useEffect(() => {
    const capturePartialLead = async () => {
      if (formData.phone && formData.name) {
        try {
          await axios.post(`${API}/leads/capture`, {
            name: formData.name,
            age: formData.age ? parseInt(formData.age) : null,
            location: formData.location,
            phone: formData.phone,
            whatsapp: formData.whatsapp,
            occupation: formData.occupation,
            health_issues: formData.health_issues,
            session_type: selectedPackage,
            stage: selectedPackage ? 'form_filled' : 'initial'
          });
        } catch (error) {
          console.debug('Partial lead capture skipped:', error);
        }
      }
    };

    const debounceTimer = setTimeout(capturePartialLead, 2000);
    return () => clearTimeout(debounceTimer);
  }, [formData, selectedPackage]);

  const packages = [
    { id: 'prana_analysis', name: 'Prana Analysis', sessions: 1, price: 1500, discount: 0, mandatory: true, description: 'Required first step' },
    { id: 'single', name: 'Single Session', sessions: 1, price: 2500, discount: 0 },
    { id: 'package_3', name: '3 Sessions', sessions: 3, price: 6500, discount: 13 },
    { id: 'package_5', name: '5 Sessions', sessions: 5, price: 10000, discount: 20, popular: true },
    { id: 'package_10', name: '10 Sessions', sessions: 10, price: 18000, discount: 28 }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPackage) {
      toast.error('Please select a session package');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const bookingData = {
        ...formData,
        session_type: selectedPackage,
        age: parseInt(formData.age),
        session_date: formData.session_date || null
      };

      const response = await axios.post(`${API}/bookings`, bookingData, config);

      toast.success('Booking created successfully!');
      setBookingSuccess({
        bookingId: response.data.booking_id,
        pkg: packages.find((p) => p.id === selectedPackage)
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to create booking. Please check all fields.');
    } finally {
      setLoading(false);
    }
  };

  const sendBookingConfirmation = () => {
    if (!bookingSuccess) return;
    const dateStr = formData.session_date
      ? new Date(formData.session_date).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
      : 'To be scheduled';
    const msg = encodeURIComponent(
      `Hi Divine Quanta team! 🙏\n\nI have just booked a healing session:\n\n📋 Booking ID: ${bookingSuccess.bookingId}\n👤 Name: ${formData.name}\n✨ Package: ${bookingSuccess.pkg?.name} (₹${bookingSuccess.pkg?.price?.toLocaleString()})\n📅 Preferred Date: ${dateStr}\n📍 Location: ${formData.location}\n\nPlease confirm my booking. Thank you!`
    );
    window.open(`https://wa.me/91${businessWhatsapp}?text=${msg}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-950 via-indigo-950 to-purple-900">
      {/* Header */}
      <header className="border-b border-purple-500/30 bg-purple-900/40 backdrop-blur-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-8 h-8 text-purple-300" />
              <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Cormorant Garamond, serif'}}>Divine Quanta</h1>
            </div>
            <Button
              data-testid="back-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-purple-200 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          {bookingSuccess ? (
            <Card className="bg-purple-900/40 backdrop-blur-lg border border-green-400/40 p-10 text-center max-w-2xl mx-auto" data-testid="booking-success-card">
              <div className="bg-green-500/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-400/50" style={{ boxShadow: '0 0 40px rgba(34,197,94,0.3)' }}>
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-3" style={{fontFamily: 'Cormorant Garamond, serif'}}>
                Booking Confirmed!
              </h2>
              <p className="text-purple-200 mb-1">
                {bookingSuccess.pkg?.name} — ₹{bookingSuccess.pkg?.price?.toLocaleString()}
              </p>
              <p className="text-purple-300 text-sm mb-8">Booking ID: {bookingSuccess.bookingId}</p>

              <div className="bg-green-900/20 border border-green-400/30 rounded-2xl p-5 mb-6 text-left">
                <p className="text-green-200 font-semibold mb-1 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" /> Step 1 — Confirm on WhatsApp
                </p>
                <p className="text-purple-200 text-sm">
                  Tap below to send your booking details to our team on WhatsApp (+91 {businessWhatsapp}). We&apos;ll confirm your session slot there.
                </p>
              </div>
              <Button
                onClick={sendBookingConfirmation}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-6 rounded-full text-lg font-semibold mb-4 whitespace-normal h-auto"
                data-testid="send-booking-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Send Booking Confirmation on WhatsApp
              </Button>

              <div className="bg-purple-900/30 border border-purple-400/30 rounded-2xl p-5 mb-6 text-left">
                <p className="text-purple-100 font-semibold mb-1">Step 2 — Complete Payment</p>
                <p className="text-purple-200 text-sm">
                  Secure your slot by paying via UPI (India) or PayPal (international).
                </p>
              </div>
              <Button
                onClick={() => navigate('/pay')}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-6 rounded-full text-lg font-semibold spiritual-glow mb-4"
                data-testid="proceed-payment-btn"
              >
                Proceed to Payment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="text-purple-300 hover:text-white"
                data-testid="success-back-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </Card>
          ) : (
          <>
          <h2 className="text-4xl font-bold text-white mb-4 text-center" style={{fontFamily: 'Cormorant Garamond, serif'}}>
            Book Your Healing Session
          </h2>
          <p className="text-purple-200 text-center mb-12">Fill in your details and choose your healing package</p>

          {/* Package Selection */}
          <div className="mb-12" data-testid="package-selection-section">
            <h3 className="text-2xl font-bold text-white mb-6">Select Your Package</h3>
            <div className="grid md:grid-cols-5 gap-4">
              {packages.map((pkg) => (
                <Card
                  key={pkg.id}
                  data-testid={`select-package-${pkg.id}`}
                  onClick={() => setSelectedPackage(pkg.id)}
                  className={`p-6 cursor-pointer relative ${
                    selectedPackage === pkg.id
                      ? 'bg-gradient-to-br from-purple-600/60 to-indigo-600/60 border-2 border-white/40 spiritual-glow'
                      : 'bg-purple-900/40 border border-purple-500/30 hover:bg-purple-900/60'
                  } backdrop-blur-lg transition-all`}
                >
                  {pkg.mandatory && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white text-purple-900 text-xs px-3 py-1 rounded-full font-bold">
                      STEP 1
                    </div>
                  )}
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white text-purple-900 text-xs px-3 py-1 rounded-full font-bold">
                      POPULAR
                    </div>
                  )}
                  {selectedPackage === pkg.id && (
                    <div className="absolute top-3 right-3 bg-white rounded-full p-1">
                      <Check className="w-4 h-4 text-purple-600" />
                    </div>
                  )}
                  {pkg.discount > 0 && (
                    <div className="absolute -top-3 right-3 bg-purple-500 text-white text-xs px-2 py-1 rounded-full">
                      Save {pkg.discount}%
                    </div>
                  )}
                  <h4 className="text-lg font-bold text-white mb-2">{pkg.name}</h4>
                  <div className="text-2xl font-bold text-white mb-2">₹{pkg.price.toLocaleString()}</div>
                  <p className="text-purple-200 text-sm">{pkg.description || `${pkg.sessions} Session${pkg.sessions > 1 ? 's' : ''}`}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Booking Form */}
          <Card className="bg-purple-900/40 backdrop-blur-lg border border-purple-500/30 p-8" data-testid="booking-form">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-purple-200">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="booking-name-input"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="bg-purple-950/50 border-purple-500/30 text-white mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="age" className="text-purple-200">Age *</Label>
                  <Input
                    id="age"
                    data-testid="booking-age-input"
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    required
                    min="1"
                    max="120"
                    className="bg-purple-950/50 border-purple-500/30 text-white mt-2"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-purple-200">Contact Number *</Label>
                  <Input
                    id="phone"
                    data-testid="booking-phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                    placeholder="Your phone number"
                    className="bg-purple-950/50 border-purple-500/30 text-white mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="whatsapp" className="text-purple-200">WhatsApp Number *</Label>
                  <Input
                    id="whatsapp"
                    data-testid="booking-whatsapp-input"
                    type="tel"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                    required
                    placeholder="WhatsApp number for reports"
                    className="bg-purple-950/50 border-purple-500/30 text-white mt-2"
                  />
                  <p className="text-purple-400 text-xs mt-1">We'll send your Aura Report here</p>
                </div>
              </div>

              <div>
                <Label htmlFor="location" className="text-purple-200">Location *</Label>
                <Input
                  id="location"
                  data-testid="booking-location-input"
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  required
                  placeholder="City, State"
                  className="bg-purple-950/50 border-purple-500/30 text-white mt-2"
                />
              </div>

              <div>
                <Label htmlFor="occupation" className="text-purple-200">Occupation *</Label>
                <Input
                  id="occupation"
                  data-testid="booking-occupation-input"
                  type="text"
                  value={formData.occupation}
                  onChange={(e) => setFormData({...formData, occupation: e.target.value})}
                  required
                  placeholder="Your profession or occupation"
                  className="bg-purple-950/50 border-purple-500/30 text-white mt-2"
                />
              </div>

              <div>
                <Label htmlFor="health_issues" className="text-purple-200">Health Issues / Concerns *</Label>
                <Textarea
                  id="health_issues"
                  data-testid="booking-health-issues-input"
                  value={formData.health_issues}
                  onChange={(e) => setFormData({...formData, health_issues: e.target.value})}
                  required
                  rows={4}
                  placeholder="Please describe any health issues, emotional concerns, or areas you'd like to focus on..."
                  className="bg-purple-950/50 border-purple-500/30 text-white mt-2 resize-none"
                />
              </div>

              <div>
                <Label htmlFor="session_date" className="text-purple-200">Preferred Session Date</Label>
                <Input
                  id="session_date"
                  data-testid="booking-session-date-input"
                  type="datetime-local"
                  value={formData.session_date}
                  onChange={(e) => setFormData({...formData, session_date: e.target.value})}
                  className="bg-purple-950/50 border-purple-500/30 text-white mt-2"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <Button
                  data-testid="cancel-booking-btn"
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  className="border-purple-500/30 text-purple-200 hover:bg-purple-900/40"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="submit-booking-btn"
                  type="submit"
                  disabled={loading || !selectedPackage}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-12 py-6 rounded-full font-semibold spiritual-glow"
                >
                  {loading ? 'Processing...' : 'Proceed to Payment'}
                </Button>
              </div>
            </form>
          </Card>

          {/* Info Box */}
          <Card className="bg-purple-900/30 backdrop-blur-lg border border-purple-500/20 p-6 mt-8" data-testid="booking-info">
            <div className="flex items-start space-x-3">
              <Calendar className="w-6 h-6 text-purple-300 mt-1" />
              <div className="text-purple-200">
                <p className="font-semibold mb-2">What happens next?</p>
                <ul className="space-y-1 text-sm">
                  <li>• You'll be redirected to secure payment</li>
                  <li>• Our therapist will contact you via WhatsApp to confirm your session</li>
                  <li>• You'll receive booking details and preparation guidelines</li>
                  <li>• Join your healing session at the scheduled time</li>
                </ul>
              </div>
            </div>
          </Card>
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
